import { useEffect, useMemo, type ReactNode } from 'react';
import { QueryProvider } from './QueryProvider';
import { WindowRegistryContext, TEAM_APPROVALS_TAB_KEY } from '@/shared/config';
import { useWindowStore } from '@/shared/model';
import { useKeyboardInsetVar } from '@/shared/lib/hooks';
import { registerDeepLinkInvalidator } from '@/shared/lib/deepLinkQueryInvalidation';
import { logger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/api';
import { usePageContext } from '@/features/page-context';
import { registerViewComponentPreloaders } from '@/widgets/CarouselDock';
import { buildWindowRegistry, WINDOW_IDS } from '../config/windowRegistry';
import { VIEW_COMPONENT_PRELOADERS } from '../config/viewComponentPreloaders';
import { invalidateViewQueries } from '../config/deepLinkQueryScopes';

// hover 청크 워밍 맵 주입 — 위젯은 pages 를 참조할 수 없어 앱 초기화가 한 번 등록한다.
// 모듈 스코프에서 부르는 이유: 렌더 전에 끝나야 첫 hover 부터 워밍이 걸린다.
registerViewComponentPreloaders(VIEW_COMPONENT_PRELOADERS);

// 딥링크 무효화 맵 주입 — 딥링크 진입 3곳(알림 패널·푸시 URL·온보딩 재생)이 놓인 레이어에서는
// entities/features 키 팩토리를 함께 볼 수 없어, 정본을 아는 app 이 한 번 등록한다.
// 여기도 모듈 스코프다: 푸시 URL 착지는 라우트 beforeLoad 라 첫 렌더보다 앞선다.
// 맵은 키 팩토리를 동적 import 하므로 비동기다 — 청크 로드가 실패해도 착지는 이미 끝난
// 뒤라 되돌릴 것이 없다. 거부를 삼키되 dev 로그는 남긴다(조용한 실패 금지).
registerDeepLinkInvalidator((view) => {
  void invalidateViewQueries(queryClient, view).catch((error: unknown) => {
    logger.warn('[deepLink] 쿼리 무효화 맵 로드 실패 — 착지는 그대로 진행됩니다.', error);
  });
});

interface ProvidersProps {
  children: ReactNode;
}

function WindowRegistryProvider({ children }: { children: ReactNode }) {
  const { teamCapabilities } = usePageContext();
  const registry = useMemo(() => buildWindowRegistry(teamCapabilities), [teamCapabilities]);
  return (
    <WindowRegistryContext.Provider value={registry}>{children}</WindowRegistryContext.Provider>
  );
}

// Why: 훅의 인셋 setState 가 앱 전체를 리렌더하지 않도록, 발행만 담당하는 leaf 로 격리한다.
function KeyboardInsetPublisher() {
  useKeyboardInsetVar();
  return null;
}

export function Providers({ children }: ProvidersProps) {
  useEffect(() => {
    // TEAM_APPROVALS_TAB_KEY 는 창 id 가 아닌 보조 탭 키 — 승인 서브탭 선택을 리로드 후에도
    // 유지하기 위해 prune 허용 목록에 포함 (구 'team-work' 창 키의 영속성 승계).
    useWindowStore.getState().pruneUnknownWindowIds([...WINDOW_IDS, TEAM_APPROVALS_TAB_KEY]);
  }, []);

  return (
    <QueryProvider>
      <KeyboardInsetPublisher />
      <WindowRegistryProvider>{children}</WindowRegistryProvider>
    </QueryProvider>
  );
}
